import projectKeys from '@/api/queries/project/keyFactory';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import useGetProjectCommitments from '@/api/queries/project/useGetProjectCommitments';
import {
  GetProjectCommitmentsQuery,
  ServicePlanPeriodCommitmentStatus,
} from '@/gql/graphql';
import ScheduledEventReadDrawer from '@/ui/components/scheduledEventReadDrawer/ScheduledEventReadDrawer';
import TaskReadCompleteDrawer from '@/ui/components/taskReadCompleteDrawer/TaskReadCompleteDrawer';
import { Badge, Button, ManageableItemsList, Modal } from '@payaca/ambrosia';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import {
  deleteServicePlanPeriodCommitment,
  updateServicePlanPeriod,
  updateServicePlanPeriodCommitment,
} from '@payaca/store/servicePlans/servicePlansActions';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import ArrangeEventModal from './ArrangeEventModal';
import ArrangeTaskModal from './ArrangeTaskModal';

const statusBadgeConfig: Record<
  ServicePlanPeriodCommitmentStatus,
  { label: string; colour: 'gray' | 'yellow' | 'green' }
> = {
  UNARRANGED: { label: 'Unarranged', colour: 'gray' },
  ARRANGED: { label: 'Arranged', colour: 'yellow' },
  FULFILLED: { label: 'Complete', colour: 'green' },
};

export type CommitmentItem = NonNullable<
  GetProjectCommitmentsQuery['projectById']['servicePlanPeriod']
>['commitments']['items'][number];

type CommitmentRow = {
  id: string;
  status: ServicePlanPeriodCommitmentStatus;
  type: 'Task' | 'Event';
  name: string;
  entityId: number | null;
};

type PanelState =
  | { activeOverlay: null; isProcessing: false }
  | {
      activeOverlay: 'arrangingCommitment';
      commitment: CommitmentItem;
      isProcessing: false;
    }
  | { activeOverlay: 'viewingTask'; taskId: number; isProcessing: false }
  | { activeOverlay: 'viewingEvent'; eventId: number; isProcessing: false }
  | {
      activeOverlay: 'deletingCommitment';
      commitmentId: string;
      isProcessing: boolean;
    }
  | { activeOverlay: 'markingPeriodComplete'; isProcessing: true };

type PanelAction =
  | {
      type: 'openArrangeCommitment';
      payload: { commitment: CommitmentItem };
    }
  | { type: 'openViewTask'; payload: { taskId: number } }
  | { type: 'openViewEvent'; payload: { eventId: number } }
  | { type: 'openDeleteCommitment'; payload: { commitmentId: string } }
  | { type: 'startMarkingPeriodComplete' }
  | { type: 'startDeleting' }
  | { type: 'close' };

const initialState: PanelState = { activeOverlay: null, isProcessing: false };

const panelReducer = (state: PanelState, action: PanelAction): PanelState => {
  switch (action.type) {
    case 'openArrangeCommitment':
      return {
        activeOverlay: 'arrangingCommitment',
        commitment: action.payload.commitment,
        isProcessing: false,
      };
    case 'openViewTask':
      return {
        activeOverlay: 'viewingTask',
        taskId: action.payload.taskId,
        isProcessing: false,
      };
    case 'openViewEvent':
      return {
        activeOverlay: 'viewingEvent',
        eventId: action.payload.eventId,
        isProcessing: false,
      };
    case 'openDeleteCommitment':
      return {
        activeOverlay: 'deletingCommitment',
        commitmentId: action.payload.commitmentId,
        isProcessing: false,
      };
    case 'startMarkingPeriodComplete':
      return { activeOverlay: 'markingPeriodComplete', isProcessing: true };
    case 'startDeleting':
      if (state.activeOverlay !== 'deletingCommitment') return state;
      return { ...state, isProcessing: true };
    case 'close':
      return initialState;
  }
};

const CommitmentsPanel: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [pagination, setPagination] = useState(PAGINATION_ARG_DEFAULTS);
  const [state, panelDispatch] = useReducer(panelReducer, initialState);
  const reduxDispatch = useDispatch();
  const queryClient = useQueryClient();

  const { project } = useGetProjectByIdOverview(projectId);
  const { commitments, isLoading } = useGetProjectCommitments(
    projectId,
    pagination
  );

  const period = project?.servicePlanPeriod;
  const canMarkComplete = period && period.status !== 'FULFILLED';

  const invalidateCommitments = () => {
    void queryClient.invalidateQueries({
      queryKey: projectKeys.commitments(projectId, pagination),
    });
  };

  const handleMarkPeriodComplete = () => {
    if (!period) return;
    panelDispatch({ type: 'startMarkingPeriodComplete' });
    reduxDispatch(
      updateServicePlanPeriod.request({
        servicePlanPeriod: {
          publicId: period.id,
          isFulfilledOverride: true,
        },
        callback: () => {
          panelDispatch({ type: 'close' });
          void queryClient.invalidateQueries({
            queryKey: projectKeys.commitments(projectId, pagination),
          });
          void queryClient.invalidateQueries({
            queryKey: projectKeys.overview(projectId),
          });
        },
        onErrorCallback: () => panelDispatch({ type: 'close' }),
      })
    );
  };

  const handleArrangeSuccess = (
    commitment: CommitmentItem,
    entityId: number
  ) => {
    panelDispatch({ type: 'close' });
    reduxDispatch(
      updateServicePlanPeriodCommitment.request({
        servicePlanPeriodCommitment: {
          publicId: commitment.id,
          entityId: entityId.toString(),
        },
        callback: () => invalidateCommitments(),
      })
    );
  };

  const rows: CommitmentRow[] =
    commitments?.items.map((commitment) => {
      switch (commitment.__typename) {
        case 'TaskCommitment': {
          const template = commitment.template;
          const templateName =
            'templateName' in template ? template.templateName : undefined;
          return {
            id: commitment.id,
            status: commitment.status,
            type: 'Task' as const,
            name: templateName || template.taskName || '',
            entityId: commitment.task?.id ? parseInt(commitment.task.id) : null,
          };
        }
        case 'ScheduledEventCommitment':
          return {
            id: commitment.id,
            status: commitment.status,
            type: 'Event' as const,
            name:
              commitment.template.templateName ||
              commitment.template.eventName ||
              '',
            entityId: commitment.scheduledEvent?.id
              ? parseInt(commitment.scheduledEvent.id)
              : null,
          };
      }
    }) ?? [];

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit,
    }));
  };

  const handleArrangeClick = (commitmentId: string) => {
    const commitment = commitments?.items.find((c) => c.id === commitmentId);
    if (commitment) {
      panelDispatch({
        type: 'openArrangeCommitment',
        payload: { commitment },
      });
    }
  };

  const handleDeleteCommitment = () => {
    if (state.activeOverlay !== 'deletingCommitment') return;
    panelDispatch({ type: 'startDeleting' });
    reduxDispatch(
      deleteServicePlanPeriodCommitment.request({
        servicePlanPeriodCommitmentId: state.commitmentId,
        callback: () => {
          panelDispatch({ type: 'close' });
          invalidateCommitments();
        },
        onErrorCallback: () => panelDispatch({ type: 'close' }),
      })
    );
  };

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Commitments"
          buttons={
            canMarkComplete ? (
              <Button
                onClick={handleMarkPeriodComplete}
                isProcessing={state.activeOverlay === 'markingPeriodComplete'}
                size="sm"
                variant="white"
              >
                Mark Period Complete
              </Button>
            ) : undefined
          }
        />
        <ManageableItemsList.Table
          items={rows}
          uniqueKey="id"
          isLoading={isLoading}
          emptyText="No commitments for this service period"
          onClickRow={(row: CommitmentRow) => {
            if (!row.entityId) return;
            if (row.type === 'Task') {
              panelDispatch({
                type: 'openViewTask',
                payload: { taskId: row.entityId },
              });
            } else {
              panelDispatch({
                type: 'openViewEvent',
                payload: { eventId: row.entityId },
              });
            }
          }}
          itemActions={(row: CommitmentRow) => [
            ...(row.status === 'ARRANGED'
              ? [
                  {
                    label: 'Mark as complete',
                    onClick: (row: CommitmentRow) => {
                      reduxDispatch(
                        updateServicePlanPeriodCommitment.request({
                          servicePlanPeriodCommitment: {
                            publicId: row.id,
                            isFulfilledOverride: true,
                          },
                          callback: () => invalidateCommitments(),
                        })
                      );
                    },
                  },
                ]
              : []),
            {
              label: 'Delete',
              onClick: (row: CommitmentRow) =>
                panelDispatch({
                  type: 'openDeleteCommitment',
                  payload: { commitmentId: row.id },
                }),
            },
          ]}
        >
          <ManageableItemsList.Table.Column<CommitmentRow, 'name'>
            header="Name"
            field="name"
          />
          <ManageableItemsList.Table.Column<CommitmentRow, 'type'>
            header="Type"
            field="type"
          />
          <ManageableItemsList.Table.Column<CommitmentRow, 'status'>
            header="Status"
            field="status"
            render={(status: ServicePlanPeriodCommitmentStatus) => {
              const config = statusBadgeConfig[status];
              return (
                <Badge variant="soft" colour={config.colour} size="sm">
                  {config.label}
                </Badge>
              );
            }}
          />
          <ManageableItemsList.Table.Column<CommitmentRow, 'status'>
            header=""
            field="status"
            render={(
              status: ServicePlanPeriodCommitmentStatus,
              row: CommitmentRow
            ) => {
              if (status !== 'UNARRANGED') return null;
              return (
                <Button
                  size="xs"
                  variant="white"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleArrangeClick(row.id);
                  }}
                >
                  Arrange
                </Button>
              );
            }}
          />
        </ManageableItemsList.Table>
        <ManageableItemsList.PaginationBar
          type="from-paginated-result"
          limit={commitments?.limit}
          offset={commitments?.offset}
          totalCount={commitments?.totalCount}
          onPageChange={handlePageChange}
        />
      </ManageableItemsList>

      {state.activeOverlay === 'arrangingCommitment' &&
        state.commitment.__typename === 'TaskCommitment' && (
          <ArrangeTaskModal
            commitment={state.commitment}
            projectId={project ? +project.id : undefined}
            onClose={() => panelDispatch({ type: 'close' })}
            onSuccess={(taskId) =>
              handleArrangeSuccess(state.commitment, taskId)
            }
          />
        )}

      {state.activeOverlay === 'arrangingCommitment' &&
        state.commitment.__typename === 'ScheduledEventCommitment' && (
          <ArrangeEventModal
            commitment={state.commitment}
            projectId={project ? +project.id : undefined}
            customerId={
              project?.customer?.id ? +project.customer.id : undefined
            }
            siteAddressId={
              project?.addresses?.site?.[0]?.id
                ? +project.addresses.site[0].id
                : undefined
            }
            onClose={() => panelDispatch({ type: 'close' })}
            onSuccess={(eventId) =>
              handleArrangeSuccess(state.commitment, eventId)
            }
          />
        )}

      <TaskReadCompleteDrawer
        taskId={
          state.activeOverlay === 'viewingTask' ? state.taskId : undefined
        }
        isOpen={state.activeOverlay === 'viewingTask'}
        onClose={() => panelDispatch({ type: 'close' })}
        onDeleteTaskSuccess={invalidateCommitments}
        onUpdateTaskSuccess={invalidateCommitments}
        hideDealLink
      />

      <ScheduledEventReadDrawer
        scheduledEventId={
          state.activeOverlay === 'viewingEvent' ? state.eventId : undefined
        }
        isOpen={state.activeOverlay === 'viewingEvent'}
        onClose={() => panelDispatch({ type: 'close' })}
        onDeleteScheduledEventSuccess={invalidateCommitments}
        onUpdateScheduledEventSuccess={invalidateCommitments}
        hideDealLink
      />

      <Modal
        isOpen={state.activeOverlay === 'deletingCommitment'}
        onClose={() => panelDispatch({ type: 'close' })}
        title="Delete Commitment"
      >
        <Modal.Body>
          <p>
            Are you sure you want to delete this commitment? This action cannot
            be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={handleDeleteCommitment}
              isProcessing={state.isProcessing}
              variant="outline"
              colour="red"
            >
              Delete
            </Button>
            <Button onClick={() => panelDispatch({ type: 'close' })}>
              Cancel
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CommitmentsPanel;
